// Campaigns — the user-facing name for what the backend calls a "workflow":
// the full pipeline from marketing brief → funnel build → video render →
// Meta publish. One row per campaign in flight or completed.
import { useEffect, useRef, useState } from 'react';
import { PALETTE } from '../palette.js';
import { Button, Pill, LiveDot } from '../components/primitives.jsx';
import ProtectedPageLoader from '../components/ProtectedPageLoader.jsx';
import {
  ConfirmationDialog,
  useConfirmationDialog,
} from '../components/ConfirmationDialog.jsx';
import { IconSpark, IconExternal, IconPlay, IconChevron } from '../components/icons.jsx';
import { api } from '../api.js';
import { safeFailureMessage } from '../safe-failure.js';

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
function daysSince(iso) {
  if (!iso) return 0;
  return Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 86_400_000));
}
function statusTone(s) {
  return s === 'done' ? 'live' : s === 'running' ? 'accent' : s === 'failed' ? 'warn' : 'neutral';
}
function tenantHeaders(companyId) {
  return companyId ? { headers: { 'x-company-id': companyId } } : undefined;
}
function activeCompanyId(session) {
  const memberships = session?.user?.memberships || [];
  return session?.user?.activeCompany?.id || memberships[0]?.companyId || '';
}
function workflowStatusTone(job) {
  const tone = job?.progress?.tone;
  if (tone === 'warn' || job?.status === 'failed' || job?.status === 'canceled') return 'warn';
  if (tone === 'success' || job?.status === 'succeeded' || job?.status === 'waiting_approval') return 'live';
  if (tone === 'active' || job?.status === 'running') return 'accent';
  return 'neutral';
}

const LIFECYCLE_ROW_TONES = {
  needs_review: 'accent',
  revising: 'accent',
  draft: 'neutral',
  archived: 'neutral',
  failed: 'warn',
};

// Rows are per workflow job, so a campaign-scoped lifecycle label is only
// truthful on the campaign's current job. A superseded run (its campaign was
// revised into a newer job) keeps describing its own finished pipeline.
export function isSupersededWorkflowRow(job, jobs = []) {
  const campaignId = job?.context?.campaign?.id || job?.campaignId;
  if (!campaignId) return false;
  return (jobs || []).some((other) => {
    if (!other || other === job || other.id === job?.id) return false;
    const otherCampaignId = other?.context?.campaign?.id || other?.campaignId;
    if (otherCampaignId !== campaignId) return false;
    const mine = Date.parse(job?.createdAt || job?.attempt?.startedAt || job?.startedAt || '') || 0;
    const theirs = Date.parse(other?.createdAt || other?.attempt?.startedAt || other?.startedAt || '') || 0;
    return theirs > mine;
  });
}

// Once generation finishes, the row's single pill must speak for the campaign
// lifecycle — that is what tells the user a decision is owed. Workflow status
// only describes the pipeline, so it would read "Complete" on a campaign
// nobody has reviewed yet.
export function campaignRowStatus(job, jobs = []) {
  const workflowStatus = job?.progress?.status || job?.status;
  const workflowPill = {
    label: job?.progress?.statusLabel || job?.status || 'Workflow',
    tone: workflowStatusTone(job),
  };
  if (!['succeeded', 'waiting_approval'].includes(String(workflowStatus || ''))) return workflowPill;
  if (isSupersededWorkflowRow(job, jobs)) return { ...workflowPill, superseded: true };
  const lifecycle = job?.context?.campaign?.lifecycle;
  const campaignStatus = job?.context?.campaign?.status;
  if (!lifecycle?.label && !campaignStatus) return workflowPill;
  const label = lifecycle?.label
    || String(campaignStatus).replace(/[_-]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  return { label, tone: LIFECYCLE_ROW_TONES[campaignStatus] || 'live' };
}
// Counters follow the same ownership as the row pill: pipeline states come
// from the workflow job, campaign states from the lifecycle. Superseded runs
// are skipped so one campaign counts once, not once per revision.
export function campaignCounters(jobs = [], isSaasSession = true) {
  const rows = Array.isArray(jobs) ? jobs : [];
  if (!isSaasSession) {
    return {
      running: rows.filter((j) => j.status === 'running').length,
      live: rows.filter((j) => j.status === 'done').length,
      awaitingReview: 0,
      // Disjoint from `interrupted` so the two problem cards partition the
      // set instead of counting the same run twice. Canceled runs are a real
      // legacy terminal status and belong with failures, not nowhere.
      failed: rows.filter((j) => ['failed', 'canceled'].includes(j.status)).length,
      interrupted: rows.filter((j) => j.status === 'interrupted').length,
    };
  }
  const current = rows.filter((j) => !isSupersededWorkflowRow(j, rows));
  return {
    running: rows.filter((j) => ['queued', 'running'].includes(j.status)).length,
    live: current.filter((j) => ['approved', 'published'].includes(j.context?.campaign?.status)).length,
    awaitingReview: current.filter((j) => j.context?.campaign?.status === 'needs_review'
      && ['succeeded', 'waiting_approval'].includes(j.status)).length,
    failed: rows.filter((j) => j.status === 'failed' || j.status === 'canceled').length,
    interrupted: 0,
  };
}

function workflowOffer(job) {
  return job?.context?.campaign?.offer || job?.input?.offer || 'Campaign';
}
function workflowProject(job) {
  return job?.context?.campaign?.projectType || job?.input?.projectType || job?.jobType || 'workflow';
}
function workflowCampaignName(job) {
  return job?.context?.campaign?.name || job?.input?.campaignName || 'Campaign';
}
function workflowBrandName(job) {
  return job?.context?.brand?.name || 'Brand';
}
function saasCampaignPath(job) {
  return ['succeeded', 'waiting_approval'].includes(job?.status)
    ? `campaign-review/${job.id}`
    : `workflow/${job.id}`;
}

function StatCard({ label, value, sub, big }) {
  return (
    <div style={{
      padding: big ? '24px 26px' : '20px 22px',
      background: PALETTE.surface, border: `1px solid ${PALETTE.line}`, borderRadius: 14,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ fontSize: 11.5, color: PALETTE.ink3, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: big ? 64 : 40, lineHeight: 1, color: PALETTE.ink, letterSpacing: '-0.02em' }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 12, color: PALETTE.ink3 }}>{sub}</div>}
    </div>
  );
}

export default function Campaigns({ navigate, route = {}, session = {} }) {
  const isSaasSession = session.user?.auth?.type === 'supabase';
  const companyId = activeCompanyId(session);
  const activeJobsContextKey = isSaasSession ? `saas:${companyId}` : 'legacy';
  const requestVersion = useRef(0);
  const jobsRef = useRef([]);
  const activeJobsContextRef = useRef(activeJobsContextKey);
  const deleteInFlightRef = useRef(new Set());
  const confirmationDialog = useConfirmationDialog();
  const [storedJobs, setStoredJobs] = useState([]);
  const [storedJobsContextKey, setStoredJobsContextKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [retryingId, setRetryingId] = useState(null);
  const jobsContextCurrent = storedJobsContextKey === activeJobsContextKey;
  const jobs = jobsContextCurrent ? storedJobs : [];
  jobsRef.current = jobs;
  activeJobsContextRef.current = activeJobsContextKey;

  useEffect(() => {
    const version = ++requestVersion.current;
    setStoredJobs([]);
    setStoredJobsContextKey('');
    setLoading(true);
    setErr(null);
    if (isSaasSession) {
      if (!companyId) {
        setErr('Active company unavailable.');
        setStoredJobsContextKey(activeJobsContextKey);
        setLoading(false);
        return () => { requestVersion.current += 1; };
      }
      api.get('/api/saas/workflows?limit=100', tenantHeaders(companyId))
        .then((r) => {
          if (version !== requestVersion.current) return;
          setStoredJobs(r.workflowJobs || []);
          setStoredJobsContextKey(activeJobsContextKey);
          setLoading(false);
        })
        .catch((e) => {
          if (version !== requestVersion.current) return;
          setErr(safeFailureMessage(e, 'campaigns'));
          setStoredJobsContextKey(activeJobsContextKey);
          setLoading(false);
        });
      return () => { requestVersion.current += 1; };
    }
    api.get('/api/workflows')
      .then((r) => {
        if (version !== requestVersion.current) return;
        setStoredJobs(r.jobs || []);
        setStoredJobsContextKey(activeJobsContextKey);
        setLoading(false);
      })
      .catch((e) => {
        if (version !== requestVersion.current) return;
        setErr(e.message);
        setStoredJobsContextKey(activeJobsContextKey);
        setLoading(false);
      });
    return () => { requestVersion.current += 1; };
  }, [isSaasSession, companyId]);

  async function handleRetry(e, j) {
    e.stopPropagation();
    if (retryingId) return;
    setRetryingId(j.id);
    try {
      await api.post(`/api/workflows/${j.brandSlug}/${j.id}/retry`);
      navigate(`building/${j.brandSlug}/${j.id}`);
    } catch (err) {
      setErr(err.message);
      setRetryingId(null);
    }
  }

  async function handleCancel(e, j) {
    e.stopPropagation();
    const contextKey = activeJobsContextKey;
    const targetKey = `${j.brandSlug}/${j.id}`;
    const approved = await confirmationDialog.request({
      eyebrow: 'Legacy workflow',
      title: `Remove ${j.brandName || j.brandSlug} workflow?`,
      description: `This permanently deletes workflow run ${j.id} from BeyondLeads. This cannot be undone.`,
      confirmLabel: 'Remove workflow',
      cancelLabel: 'Keep workflow',
      tone: 'danger',
    });
    if (!approved) return;
    const targetStillCurrent = jobsRef.current.some((candidate) => (
      candidate?.brandSlug === j.brandSlug && candidate?.id === j.id
    ));
    if (
      activeJobsContextRef.current !== contextKey
      || !targetStillCurrent
      || deleteInFlightRef.current.has(targetKey)
    ) return;
    deleteInFlightRef.current.add(targetKey);
    try {
      await api.del(`/api/workflows/${j.brandSlug}/${j.id}`);
      if (activeJobsContextRef.current !== contextKey) return;
      setStoredJobs((prev) => prev.filter((x) => !(x.brandSlug === j.brandSlug && x.id === j.id)));
    } catch (err) {
      if (activeJobsContextRef.current === contextKey) setErr(err.message);
    } finally {
      deleteInFlightRef.current.delete(targetKey);
    }
  }

  const { running, live, awaitingReview, failed, interrupted } = campaignCounters(jobs, isSaasSession);
  const latestPath = jobs[0]
    ? isSaasSession
      ? saasCampaignPath(jobs[0])
      : `building/${jobs[0].brandSlug}/${jobs[0].id}`
    : null;
  const showInitialCampaignLoading = (loading || !jobsContextCurrent) && jobs.length === 0;
  const legacyLinkUnavailable = isSaasSession
    && route?.query?.notice === 'workflow-link-unavailable';

  return (
    <div className="campaigns-page" style={{ flex: 1, background: PALETTE.bg, overflow: 'auto', fontFamily: 'Inter, sans-serif', color: PALETTE.ink, height: '100vh' }}>
      <div className="campaigns-toolbar" style={{
        minHeight: 56, padding: '0 32px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', borderBottom: `1px solid ${PALETTE.lineSoft}`,
        background: PALETTE.bg, position: 'sticky', top: 0, zIndex: 3,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ fontSize: 13, color: PALETTE.ink3 }}>Live campaigns</div>
          <Pill tone="live"><LiveDot/> {running} running · {live} live</Pill>
          {awaitingReview ? <Pill tone="accent">{awaitingReview} awaiting review</Pill> : null}
        </div>
        <Button variant="accent" size="sm" onClick={() => navigate('wizard')}>
          <IconSpark size={12}/> New campaign
        </Button>
      </div>

      <div className="campaigns-content" style={{ padding: '36px 40px', maxWidth: 1200, margin: '0 auto' }}>
        {legacyLinkUnavailable && (
          <div role="status" style={legacyLinkNoticeStyle}>
            We couldn't open that saved campaign link. Open the campaign from this company's current list.
          </div>
        )}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: PALETTE.accent, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 14 }}>
            All campaigns · {jobs.length} total
          </div>
          <h1 className="campaigns-title" style={{
            fontFamily: 'Instrument Serif, serif', fontWeight: 400,
            fontSize: 56, lineHeight: 1.02, letterSpacing: '-0.02em', margin: 0,
          }}>
            <em style={{ color: PALETTE.accent, fontStyle: 'italic' }}>{live}</em> campaigns live
            <span style={{ color: PALETTE.ink3 }}>
              {awaitingReview ? `, ${awaitingReview} awaiting review` : ''}
              {running ? `, ${running} building.` : '.'}
            </span>
          </h1>
        </div>

        {showInitialCampaignLoading ? (
          <ProtectedPageLoader />
        ) : (
          <>
            <div className="campaigns-stat-grid">
              <StatCard big label="Live campaigns" value={live} sub={isSaasSession ? 'approved or published' : 'completed runs'} />
              <StatCard
                label={isSaasSession ? 'Awaiting review' : 'Interrupted'}
                value={isSaasSession ? awaitingReview : interrupted}
                sub={isSaasSession
                  ? (awaitingReview ? 'need your decision' : 'nothing to review')
                  : 'stopped before finishing'}
              />
              <StatCard label="Building" value={running} sub={running ? 'workflows in progress' : 'nothing in flight'}/>
              <StatCard label="Needs attention" value={failed} sub="failed or canceled"/>
              <StatCard label="Total runs" value={jobs.length} sub="all-time"/>
            </div>

            <div className="campaigns-table" style={{ background: PALETTE.surface, border: `1px solid ${PALETTE.line}`, borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${PALETTE.lineSoft}` }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 500, color: PALETTE.ink, letterSpacing: '-0.01em' }}>Your campaigns</div>
                  <div style={{ fontSize: 12, color: PALETTE.ink3, marginTop: 2 }}>Click a row to open progress or review generated assets.</div>
                </div>
              </div>
              <div className="campaigns-table-head" style={{
                display: 'grid', gridTemplateColumns: '1.4fr 1fr 0.8fr 0.6fr 0.7fr auto',
                gap: 16, padding: '10px 20px', background: PALETTE.surfaceAlt,
                fontSize: 10.5, color: PALETTE.ink3, letterSpacing: '0.06em', textTransform: 'uppercase',
                borderBottom: `1px solid ${PALETTE.lineSoft}`,
              }}>
                <span>Brand</span><span>Offer / project</span><span>Status</span><span>Started</span><span>{isSaasSession ? 'Progress' : 'Duration'}</span><span/>
              </div>

              {err && <div style={{ padding: 24, color: '#A33' }}>{err}</div>}
              {!loading && jobs.length === 0 && (
                <div style={{ padding: 32, textAlign: 'center', color: PALETTE.ink3 }}>
                  No campaigns yet. <button onClick={() => navigate('wizard')} style={{
                    background: 'none', border: 'none', color: PALETTE.accent, cursor: 'pointer',
                    fontFamily: 'inherit', fontSize: 'inherit', textDecoration: 'underline', textUnderlineOffset: 3,
                  }}>Start your first one →</button>
                </div>
              )}

              {jobs.map((j) => isSaasSession ? (
                <div
                  key={j.id}
                  className="campaigns-row"
                  role="button"
                  tabIndex={0}
                  aria-label={`Open ${workflowCampaignName(j)}`}
                  onClick={() => navigate(saasCampaignPath(j))}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') navigate(saasCampaignPath(j));
                  }}
                  style={{
                  display: 'grid', gridTemplateColumns: '1.4fr 1fr 0.8fr 0.6fr 0.7fr auto',
                  gap: 16, alignItems: 'center', padding: '14px 20px',
                  borderBottom: `1px solid ${PALETTE.lineSoft}`, cursor: 'pointer', fontSize: 13,
                }}>
                  <div className="campaigns-row-primary" style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: PALETTE.ink, overflowWrap: 'anywhere' }}>{workflowCampaignName(j)}</div>
                    <div style={{ fontSize: 11, color: PALETTE.ink3, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{workflowBrandName(j)}</div>
                  </div>
                  <div className="campaigns-row-context" style={{ color: PALETTE.ink2, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {workflowOffer(j)}
                    <span style={{ color: PALETTE.ink3 }}> · {workflowProject(j)}</span>
                  </div>
                  <div className="campaigns-row-status">
                    {(() => {
                      const rowStatus = campaignRowStatus(j, jobs);
                      return (
                        <Pill tone={rowStatus.tone} style={{ justifySelf: 'start' }}>
                          {rowStatus.superseded ? `${rowStatus.label} · superseded` : rowStatus.label}
                        </Pill>
                      );
                    })()}
                  </div>
                  <div className="campaigns-row-started" style={{ color: PALETTE.ink3, fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>{fmtDate(j.attempt?.startedAt || j.startedAt || j.createdAt)}</div>
                  <div className="campaigns-row-progress" style={{ color: PALETTE.ink3, fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>
                    {Number(j.progress?.percent ?? j.progressPercent ?? 0)}%
                  </div>
                  <div className="campaigns-row-action" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <IconChevron size={14}/>
                  </div>
                </div>
              ) : (
                <div key={`${j.brandSlug}/${j.id}`} onClick={() => navigate(`building/${j.brandSlug}/${j.id}`)} style={{
                  display: 'grid', gridTemplateColumns: '1.4fr 1fr 0.8fr 0.6fr 0.7fr auto',
                  gap: 16, alignItems: 'center', padding: '14px 20px',
                  borderBottom: `1px solid ${PALETTE.lineSoft}`, cursor: 'pointer', fontSize: 13,
                }}>
                  <div>
                    <div style={{ fontWeight: 500, color: PALETTE.ink }}>{j.brandName || j.brandSlug}</div>
                    <div style={{ fontSize: 11, color: PALETTE.ink3, marginTop: 2, fontFamily: 'JetBrains Mono, monospace' }}>{j.id}</div>
                  </div>
                  <div style={{ color: PALETTE.ink2 }}>
                    {j.offer || '—'}
                    <span style={{ color: PALETTE.ink3 }}> · {j.projectType || '—'}</span>
                  </div>
                  <Pill tone={statusTone(j.status)} style={{ justifySelf: 'start' }}>{j.status}</Pill>
                  <div style={{ color: PALETTE.ink3, fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>{fmtDate(j.startedAt || j.createdAt)}</div>
                  <div style={{ color: PALETTE.ink3, fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>
                    {j.durationMs ? `${Math.round(j.durationMs / 1000)}s` : j.status === 'running' ? 'live' : '—'}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {['failed', 'interrupted', 'canceled'].includes(j.status) && (
                      <>
                        <Button variant="accent" size="sm" onClick={(e) => handleRetry(e, j)} disabled={retryingId === j.id}>
                          {retryingId === j.id ? '…' : 'Retry'}
                        </Button>
                        <Button variant="danger" size="sm" onClick={(e) => handleCancel(e, j)}>
                          Cancel
                        </Button>
                      </>
                    )}
                    <IconChevron size={14}/>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {!showInitialCampaignLoading && (
        <div className="campaigns-followup-grid">
          <div style={{ background: PALETTE.surface, border: `1px solid ${PALETTE.line}`, borderRadius: 14, padding: 24 }}>
            <Pill tone="warn" style={{ marginBottom: 12 }}>Coming soon</Pill>
            <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: 28, lineHeight: 1.1, marginBottom: 8 }}>Lead inbox</div>
            <p style={{ color: PALETTE.ink2, fontSize: 14, lineHeight: 1.55 }}>
              Once the Meta integration is wired into the dashboard, qualified leads from your funnel will stream in here with quiz score, budget band and a one-tap call button.
            </p>
          </div>
          <div style={{ background: PALETTE.ink, color: '#FAF8F5', borderRadius: 14, padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <IconSpark size={14} style={{ color: PALETTE.accent }}/>
              <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(250,248,245,0.6)' }}>What's next</div>
            </div>
            <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: 24, lineHeight: 1.2, marginBottom: 12 }}>
              Open the latest campaign and review the funnel + videos before publishing to Meta.
            </div>
            <Button variant="accent" size="sm" onClick={() => latestPath && navigate(latestPath)} disabled={!latestPath}
              style={{ background: PALETTE.accent, color: '#fff', border: 'none' }}>
              Open latest <IconExternal size={11}/>
            </Button>
          </div>
        </div>
        )}
      </div>
      <ConfirmationDialog {...confirmationDialog.dialogProps} />
    </div>
  );
}

const legacyLinkNoticeStyle = {
  marginBottom: 22,
  padding: '14px 16px',
  border: `1px solid ${PALETTE.warnLine || PALETTE.line}`,
  borderRadius: 8,
  background: PALETTE.warnSoft || PALETTE.surfaceAlt,
  color: PALETTE.ink2,
  fontSize: 13,
  lineHeight: 1.5,
};
